/* ===================================================================
   FinishSetupCard -- the persistent Dashboard nudge shown when setup is
   incomplete AND reminders aren't turned off.

   Faithful port of the prototype's `showSetupCard`
   ("Rhody Reviews.dc.html" desktop 374-402 / mobile 1934-1957): a white card
   with a checklist glyph, "Finish setting up {business}", a "Next: {step}"
   line, a "Resume setup" primary + an x, a progress bar, and -- when the x is
   clicked -- a deliberate "turn off reminders" confirmation strip.

   REAL-STATE GATING:
     - `showSetupCard = setup incomplete && !remindersOff` (prototype line
       3632). Completeness comes from useSetupProgress(), which DERIVES it
       from real account signals (GBP/customers/voice/requests) -- no
       fabricated onboarding table. Reconciled with FirstRun.tsx (same
       signals); the old Dashboard never rendered a setup card, so this is
       the single, non-duplicated home for it.
     - Reminders-off is a per-client localStorage dismissal (useLocalFlag).
     - While the signals load, we render nothing (avoid flashing the card on
       an already-configured account).

   "Resume setup" opens the AppShell-mounted onboarding overlay via a window
   CustomEvent (rhody:open-onboarding); the card lives on the Dashboard route
   while the overlay is a portal above every screen, so an event is the clean
   seam.
   =================================================================== */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMe, useSetupProgress } from '../../hooks';
import { useLocalFlag } from '../../hooks/useLocalFlag';
import '../../styles/overlays.css';

export function FinishSetupCard() {
  const navigate = useNavigate();
  const { data: me } = useMe();
  const progress = useSetupProgress();
  const [remindersOff, setRemindersOff] = useLocalFlag('reminders-off', me?.client_id);
  const [confirmOff, setConfirmOff] = useState(false);

  // Do not flash the card before we have a definitive read, and never show it
  // once setup is complete or reminders are off.
  if (progress.loading) return null;
  if (!progress.incomplete || remindersOff) return null;

  const bizName = me?.business_name ?? 'your business';
  const nextTitle = progress.nextStep?.title ?? '';

  return (
    <div className="rh-setup">
      <div className="rh-setup__row">
        <div className="rh-setup__icon" aria-hidden="true">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path d="M9 11l3 3L22 4" />
            <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
          </svg>
        </div>
        <div className="rh-setup__body">
          <div className="rh-setup__title">Finish setting up {bizName}</div>
          <div className="rh-setup__next">
            Next: <strong>{nextTitle}</strong>
          </div>
        </div>
        <div className="rh-setup__tools">
          <button
            className="rh-setup__resume"
            onClick={() => navigate('/onboarding')}
          >
            Resume setup
          </button>
          <button
            className="rh-setup__x"
            title="Dismiss"
            aria-label="Turn off setup reminders"
            onClick={() => setConfirmOff(true)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <div className="rh-setup__progress">
        <div className="rh-setup__track">
          <div className="rh-setup__fill" style={{ width: `${progress.pct}%` }} />
        </div>
        <span className="rh-setup__pct">
          {progress.doneCount} of {progress.total} complete
        </span>
      </div>

      {confirmOff && (
        <div className="rh-setup__confirm">
          {/* Copy fixed 2026-08-06 (Brad). Three problems: it pointed at
              Tutorials, which is now hidden (and never restarted setup anyway
              -- "Resume setup" above does that); it said "Rhody" instead of
              "Rhody Reviews" (Hard Rule #8, the product is the actor); and it
              used an em dash, which AI-drafted copy does not. */}
          <div className="rh-setup__confirm-text">
            Stop reminding me? Your setup isn&apos;t finished, and Rhody Reviews won&apos;t
            collect reviews until it is.
          </div>
          <div className="rh-setup__confirm-btns">
            <button className="rh-setup__keep" onClick={() => setConfirmOff(false)}>
              Keep reminders
            </button>
            <button
              className="rh-setup__off"
              onClick={() => {
                setRemindersOff(true);
                setConfirmOff(false);
              }}
            >
              Turn off reminders
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
